import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, useCallback, useEffect, useState } from "react";
import { formatCountLabel } from "../../../utils/text";
import { FilterChip } from "../FilterChip";
import TreeSelect from "../TreeSelect/TreeSelect";
import { transformToTree } from "../TreeSelect/utils";

interface Props {
  categories: { id: string; name: string; parentId: string }[];
  includeRoot: boolean;
  selectedItems: string[];
  onSelectedItems: (selectedItems: string[]) => void;
  highlighted?: boolean;
  id?: string;
}

export const SpendCategoryFilterChip: FC<Props> = ({
  categories,
  includeRoot,
  selectedItems,
  onSelectedItems,
  highlighted,
  id = "spend-category",
}) => {
  const [tempSelectedSpendCategories, setTempSelectedSpendCategories] =
    useState<string[]>(selectedItems);
  const [selectedSpendCategoryCount, setSelectedSpendCategoryCount] = useState(
    selectedItems.length
  );

  useEffect(() => {
    setTempSelectedSpendCategories(selectedItems);
    setSelectedSpendCategoryCount(selectedItems.length);
  }, [selectedItems]);

  const applyFilterChanges = useCallback(
    (itemsToApply: string[], countToApply: number) => {
      onSelectedItems(itemsToApply);
      setSelectedSpendCategoryCount(countToApply);
    },
    [onSelectedItems]
  );

  const handleTreeSelectChange = useCallback(
    (newSelectedTreeItems: string[]) => {
      setTempSelectedSpendCategories(newSelectedTreeItems);
    },
    []
  );

  const tree = transformToTree(categories, (category) => category.name);
  const options = includeRoot ? tree : tree[0]?.children || [];

  return (
    <FilterChip
      id={id}
      label="Spend Category"
      getSelectedCount={() => selectedSpendCategoryCount}
      highlighted={highlighted}
      popoverOptions={{
        disableActionButtons: true,
        //** Selected IT Spend Categories will be applied when user unfocus's from the input
        onClose: () => {
          applyFilterChanges(
            tempSelectedSpendCategories,
            selectedSpendCategoryCount
          );
        },
      }}
      renderLabel={(label, count) => (
        <Typography component="span" color="textSecondary">
          {formatCountLabel(label, count)}
        </Typography>
      )}
    >
      <Stack direction="column" gap={1}>
        <TreeSelect
          items={options}
          selectedItems={tempSelectedSpendCategories}
          onSelectItems={handleTreeSelectChange}
          placeholder={"Search Spend Categories"}
          noOptionsMessage={"No Results"}
        />

        <Stack alignSelf="start">
          <Button
            variant="text"
            color="inherit"
            size="small"
            onClick={() => handleTreeSelectChange([])}
          >
            clear all entries
          </Button>
        </Stack>
      </Stack>
    </FilterChip>
  );
};
